import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@material-ui/core/Box';
import {
  deleteBracketLibrary,
  fetchBracketLibraries,
  fetchBracketLibraryAssignableMembers,
  resetBracketLibraries,
  updateBracketLibrary,
  uploadBracketLibrary
} from '../../../../reducers/bracketLibraries';
import LoadingIndicator from '../../../../components/LoadingIndicator';
import EditDialog from './EditDialog';
import Can from '../../../../components/Can';
import UploadDialog from './UploadDialog';
import FileList from './FileList';
import InventoryDialog from './InventoryDialog';
import BracketLibrariesApiService from '../../../../services/api/bracketLibraries';
import hasPermission from '../../../../selectors/hasPermission';
import Changelog from './InventoryDialog/Changelog';

const Main = ({ organization }) => {
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [replacing, setReplacing] = useState(null);
  const [selectedBracket, setSelectedBracket] = useState(null);
  const [selectedBracketInventory, setSelectedBracketInventory] = useState(null);
  const [showChangelog, setShowChangelog] = useState(null);

  const {
    fetched = false,
    fetching = false,
    items = [],
    assignableMembers = [],
    uploading = [],
    removing = [],
    updating = false
  } = useSelector(state => {
    return state.bracketLibraries.organizations[organization.id] || {};
  });
  const filteredItems = items.filter(item => !item.replaced_by_id);
  const dispatch = useDispatch();
  const canManageInventory = useSelector(state =>
    hasPermission(state, {
      permissions: ['bracket-libraries.manage-inventory', 'bracket-libraries.manage']
    })
  );
  const canAssignMembers = useSelector(state =>
    hasPermission(state, {
      permissions: [
        'bracket-libraries.manage',
        'bracket-libraries.update',
        'organization.bracket-libraries.update'
      ],
      organizationId: organization.id
    })
  );
  const assignableMembersRequestedFor = useRef(null);

  const bracketLibrariesApiService = new BracketLibrariesApiService();

  const handleSaveCallback = replaces => files => {
    const promises = files.map(file => {
      return dispatch(
        uploadBracketLibrary(organization.id, { ...file, replaces_id: replaces?.id })
      );
    });
    Promise.all(promises).then(() => {
      setShowUploadPopup(false);
      setReplacing(null);
    });
  };

  const handleBracketUpdate = (id, data) => {
    dispatch(updateBracketLibrary(organization.id, id, data))
      .then(() => {
        setSelectedBracket(null);
      })
      .catch(() => {});
  };

  const handleDelete = id => {
    dispatch(deleteBracketLibrary(organization.id, id));
  };

  const handleDownload = id => {
    bracketLibrariesApiService.getBracketLibraryDownloadUrl(organization.id, id).then(({ url }) => {
      window.open(url);
    });
  };

  const isFileAlreadyUploadedCallback = (except = null) => fileName => {
    return (
      filteredItems.length > 0 &&
      filteredItems.filter(
        item => item.file_name === fileName && (!except || except.id !== item.id)
      ).length > 0
    );
  };

  const handleRefresh = e => {
    e.preventDefault();
    dispatch(resetBracketLibraries(organization.id));
  };

  useEffect(() => {
    if (fetched || fetching) {
      return;
    }
    dispatch(fetchBracketLibraries(organization.id));
  }, [fetched, organization, dispatch, fetching]);

  useEffect(() => {
    if (!canAssignMembers || assignableMembersRequestedFor.current === organization.id) {
      return;
    }
    assignableMembersRequestedFor.current = organization.id;
    dispatch(fetchBracketLibraryAssignableMembers(organization.id));
  }, [canAssignMembers, organization.id, dispatch]);

  return (
    <>
      {showChangelog !== null && (
        <Changelog
          organizationId={organization.id}
          bracketLibraryId={showChangelog}
          open
          onClose={() => setShowChangelog(null)}
        />
      )}
      <UploadDialog
        title="Upload Bracket Libraries"
        uploading={uploading.length > 0}
        handleSave={handleSaveCallback()}
        retrieveDownloadUrl={name => {
          const api = new BracketLibrariesApiService();
          return api.getUploadUrlForBracketLibraries(organization.id, name);
        }}
        organization={organization}
        open={showUploadPopup}
        handleClose={() => setShowUploadPopup(false)}
        isFileAlreadyUploaded={isFileAlreadyUploadedCallback()}
      />
      {replacing && (
        <UploadDialog
          title={`Replacing ${replacing.label}`}
          uploading={uploading.length > 0}
          handleSave={handleSaveCallback(replacing)}
          retrieveDownloadUrl={name => {
            const api = new BracketLibrariesApiService();
            return api.getUploadUrlForBracketLibraries(organization.id, name);
          }}
          organization={organization}
          open={!!replacing}
          handleClose={() => setReplacing(null)}
          isFileAlreadyUploaded={isFileAlreadyUploadedCallback(replacing)}
        />
      )}

      {selectedBracket && (
        <EditDialog
          open={!!selectedBracket}
          setOpen={() => setSelectedBracket(null)}
          name={selectedBracket ? selectedBracket.label : ''}
          bracket={selectedBracket}
          users={canAssignMembers ? assignableMembers : []}
          onBracketUpdate={data => handleBracketUpdate(selectedBracket.id, data)}
        />
      )}
      {organization && selectedBracketInventory && (
        <InventoryDialog
          organization={organization}
          bracket={selectedBracketInventory}
          open={!!selectedBracketInventory}
          readOnly={!canManageInventory}
          setOpen={count => {
            setSelectedBracketInventory(null);
            if (count > 0) {
              dispatch(resetBracketLibraries(organization.id));
            }
          }}
        />
      )}
      <Grid container>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" mt={1} mb={1}>
            <Can
              permissions={['bracket-libraries.manage']}
              organizationId={organization.id}
              yes={() => (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  onClick={() => setShowUploadPopup(true)}
                  disabled={fetching || updating}
                >
                  Add Library (Zip File)
                </Button>
              )}
            />
            <Button
              style={{ marginLeft: 4 }}
              type="submit"
              variant="contained"
              color="primary"
              onClick={handleRefresh}
              disabled={fetching || updating}
            >
              Refresh
            </Button>
          </Box>
        </Grid>
        {fetching && <LoadingIndicator />}
        {filteredItems && (
          <Grid item xs={12}>
            <FileList
              data={filteredItems}
              onDelete={handleDelete}
              onDownload={handleDownload}
              onEdit={bracket => setSelectedBracket(bracket)}
              removingItems={removing}
              organizationId={organization.id}
              onOpenInventory={bracket => {
                setSelectedBracketInventory(bracket);
              }}
              showChangelog={bracket => setShowChangelog(bracket.id)}
              onUpdate={handleBracketUpdate}
              onReplace={bracket => setReplacing(bracket)}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

Main.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  organization: PropTypes.object.isRequired
};

Main.defaultProps = {};

export default Main;
